import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { isEmptySearchParams } from '../../utils/search-url-params-utils';
import { BloomreachProductType } from './use-search-types';
import { useSearch } from './useSearch';
import type { UseSearchResult } from './use-search-types';

interface UseSearches {
    currentProductType: BloomreachProductType;
    searchesToShow: UseSearchResult[];
    searchByType: Record<BloomreachProductType, UseSearchResult>;
    currentSearch: UseSearchResult;
    hardgoodSearch: UseSearchResult;
    culinarySearch: UseSearchResult;
    recipeSearch: UseSearchResult;
    totalResults: number;
    isLoading: boolean;
    isFetching: boolean;
    isHardgoodActive: boolean;
    onChangeQuery: (query: string) => void;
}

export const useSearches = (): UseSearches => {
    const location = useLocation();

    const hardgoodSearch = useSearch(BloomreachProductType.hardgood);
    const culinarySearch = useSearch(BloomreachProductType.culinary);
    const recipeSearch = useSearch(BloomreachProductType.recipe);

    return useMemo<UseSearches>(() => {
        const searches = [hardgoodSearch, culinarySearch, recipeSearch];

        const searchesToShow = searches.filter(
            ({ total, isLoading, productType, searchParams }) =>
                total ||
                isLoading ||
                (productType === BloomreachProductType.hardgood &&
                    !isEmptySearchParams(searchParams)),
        );

        const searchByType: Record<BloomreachProductType, UseSearchResult> = {
            [BloomreachProductType.hardgood]: hardgoodSearch,
            [BloomreachProductType.recipe]: recipeSearch,
            [BloomreachProductType.culinary]: culinarySearch,
        };

        const onChangeQuery = (query: string): void =>
            searches.forEach((search) => search.onChangeParam('q', query, false));

        const currentProductType =
            (new URLSearchParams(location.search).get('productType') as BloomreachProductType) ??
            BloomreachProductType.hardgood;

        const currentSearch = searchByType[currentProductType];

        return {
            currentProductType,
            searchesToShow,
            searchByType,
            currentSearch,
            hardgoodSearch,
            culinarySearch,
            recipeSearch,
            totalResults: searchesToShow.reduce((acc, { total }) => acc + total, 0),
            isLoading: searches.some(({ isLoading, items }) => isLoading && items.length === 0),
            isFetching: searches.some(({ isLoading, isFetching }) => !isLoading && isFetching),
            isHardgoodActive: currentProductType === BloomreachProductType.hardgood,
            onChangeQuery,
        };
    }, [hardgoodSearch, culinarySearch, recipeSearch, location]);
};
